/**
 * 1.3.0 Intake's data hooks (stream P4's file). Keys: queryKeys.intake*().
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  IntakeAcceptResult, IntakeChip, IntakeDestination, IntakeListDTO, IntakeNoteDTO, IntakeSubmissionDTO, IntakeSummaryDTO, ItemState, SubmissionKind,
} from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';

/** GET /api/v1/intake/:id: the DTO plus what the reviewer works with (services/api/src/modules/portal/intake/queries.ts). */
export interface IntakeSubmissionDetail extends IntakeSubmissionDTO {
  versions: Array<{ versionId: string; number: number; filename: string; sizeBytes: number | null; status: string; qcStatus: string | null; createdAt: string; current: boolean; itemState: ItemState | null; reason: string | null }>;
  attachments: Array<{ id: string; itemId: string | null; kind: string; filename: string; mimeType: string; sizeBytes: number; url: string; createdAt: string }>;
  destinationDefaults: IntakeDestination;
  mergeFields: string[];
  stillProcessing: boolean;
  accept: { complete: boolean; withMissing: boolean; why: string | null };
  libraryAsset: { id: string; assetCode: string; title: string } | null;
}

/**
 * The Intake rail badge: submissions waiting for review (GET /api/v1/intake/summary), for portal.review holders only.
 */
export function useIntakeSummary(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.intakeSummary(), queryFn: () => api<IntakeSummaryDTO>('/api/v1/intake/summary'),
    enabled, refetchInterval: 30_000,
  });
}

/** groupId: 1.4.0 (G3), the Group filter. */
export interface IntakeFilters { chip: IntakeChip; companyId?: string; kind?: SubmissionKind; overdue?: boolean; q?: string; groupId?: string }

const listParams = (f: IntakeFilters) => {
  const p = new URLSearchParams({ chip: f.chip });
  if (f.companyId) p.set('companyId', f.companyId);
  if (f.kind) p.set('kind', f.kind);
  if (f.overdue) p.set('overdue', 'true');
  if (f.q?.trim()) p.set('q', f.q.trim());
  if (f.groupId) p.set('groupId', f.groupId);
  return p.toString();
};

/** The Intake list, a page at a time (keyset cursor), refreshed every 30 seconds. */
export function useIntakeList(f: IntakeFilters) {
  const params = listParams(f);
  const q = useInfiniteQuery({
    queryKey: queryKeys.intakeList(params),
    queryFn: ({ pageParam }) => api<IntakeListDTO>(`/api/v1/intake?${params}&limit=50${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 30_000,
  });
  const pages = q.data?.pages ?? [];
  return {
    ...q,
    items: pages.flatMap((p) => p.items),
    counts: pages[0]?.counts ?? null,
    hasMore: Boolean(q.hasNextPage),
    loadMore: () => void q.fetchNextPage(),
    loadingMore: q.isFetchingNextPage,
  };
}

export function useIntakeSubmission(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.intakeSubmission(id), queryFn: () => api<IntakeSubmissionDetail>(`/api/v1/intake/${id}`),
    enabled: Boolean(id), refetchInterval: (q) => (q.state.data?.stillProcessing ? 4000 : 30_000),
  });
}

export function useIntakeNotes(id: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.intakeNotes(id), queryFn: () => api<{ items: IntakeNoteDTO[] }>(`/api/v1/intake/${id}/notes`),
    enabled: Boolean(id), refetchInterval: 30_000,
  });
}

/** Placeholders an open upload can be linked to. */
export function useIntakePlaceholders(q: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.intake(), 'placeholders', q],
    queryFn: () => api<{ items: Array<{ id: string; assetCode: string; title: string }> }>(`/api/v1/intake/placeholders?q=${encodeURIComponent(q)}`),
    enabled,
  });
}

/** Every Intake mutation refreshes all of Intake (the list, the badge, the submission and its notes). */
export function useIntakeAction<B, R = unknown>(fn: (body: B) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.intake() }),
  });
}

export const intakeApi = {
  decide: (id: string, itemId: string, body: { decision: 'accept' } | { decision: 'reject'; reason: string } | { decision: 'waive'; reason?: string } | { decision: 'reopen' }) =>
    api<{ submission: IntakeSubmissionDetail; accepted: IntakeAcceptResult | null }>(`/api/v1/intake/${id}/items/${itemId}/decision`, { method: 'POST', json: body }),
  accept: (id: string, body: { allowMissing?: boolean; destination?: IntakeDestination }) =>
    api<IntakeAcceptResult>(`/api/v1/intake/${id}/accept`, { method: 'POST', json: body }),
  bulkAccept: (submissionIds: string[]) =>
    api<{ accepted: IntakeAcceptResult[]; refused: Array<{ submissionId: string; code: string; message: string }> }>('/api/v1/intake/accept', { method: 'POST', json: { submissionIds } }),
  reject: (id: string, reason: string) => api<{ ok: true }>(`/api/v1/intake/${id}/reject`, { method: 'POST', json: { reason } }),
  destination: (id: string, body: IntakeDestination) => api<IntakeSubmissionDetail>(`/api/v1/intake/${id}/destination`, { method: 'PATCH', json: body }),
  note: (id: string, body: { body: string; itemId: string | null; internal: boolean }) => api<IntakeNoteDTO>(`/api/v1/intake/${id}/notes`, { method: 'POST', json: body }),
};
